<template>
    <div v-if="!isSupplier" class="card-body d-flex flex-wrap" style="gap: .5rem" size="full">
        <Button color="teal" @click="assignValuesTarget = [...selectedProducts]">
            <i class="icon-equalizer3 mr-2"></i>
            {{ $t("buttons.assign_values_to_attributes") }}
        </Button>
        <Button color="teal" @click="$refs.AssignAttributesFromIntegrationModal.show(selectedProducts, tableHandle.total, tableHandle.search, productsTable.queryParams)" :disabled="!selectedProducts.length">
            <i class="icon-share3 mr-2"></i>
            {{ $t("buttons.assign_values_from_example_integration") }}
        </Button>
        <Button color="teal" @click="$refs.AssignValuesBasedOnProductInformation.show(selectedProducts)">
            <i class="icon-database2 mr-2"></i>
            {{ $t("buttons.assign_values_based_on_product_information") }}
        </Button>
    </div>
    <Table
        :handle="tableHandle"
        :url="productsListUrl"
        :columns="productsTable.columns"
        :has-checkbox="!isSupplier"
        @click:filters-button="$refs.CategoryProductsFiltersModal.show()"
        @update:selected-values="selectedProducts = $event"
        v-model:orders="productsTable.orders"
        allow-column-settings
        has-filters
        :active-filters-count="activeFiltersCount"
        @click:clear-filters="clearFilters"
    >
        <template v-if="!isSupplier" #actionDropdownItems>
            <DropdownItem @click="$refs.EditProductNameModal.show(selectedProducts)">{{ $t("buttons.edit_product_name") }}</DropdownItem>
            <DropdownItem @click="$refs.GenerateProductKeyWordsModal.show(selectedProducts)">{{ $t("generateProductKeyWordsModal.button") }}</DropdownItem>
        </template>
        <template #td-id="{ row }">
            <AppNullableCell>
                <template #default v-if="row.id">
                    <router-link class="d-block long-string" :to="{name: 'Product', params: { integrationProductUuid: row.uuid }}">
                        {{ row.id }}
                    </router-link>
                </template>
            </AppNullableCell>
        </template>
        <template #td-image="{ row }">
            <ImageZoomComponent
                :url="row.image_url ?? PLACEHOLDER_IMAGE_URL"
                :alt="row.name"
            />
        </template>
        <template #td-name="{row}">
            <AppNullableCell>
                <template #default v-if="row.name">
                    <span class="long-string" :title="row.name">{{ row.name }}</span>
                </template>
            </AppNullableCell>
        </template>
        <template v-for="filter in filters" #[`td-${filter.uuid}`]="{ row }">
            <AppNullableCell>
                <template #default v-if="findFilterItemNameInProduct(filter, row)">
                    <span class="filter-item-string" :title="findFilterItemNameInProduct(filter, row)">
                        {{ findFilterItemNameInProduct(filter, row) }}
                    </span>
                </template>
            </AppNullableCell>
        </template>
        <template #td-created_at="{ row }">
            <span>{{ DateFormatter.formatDateTime(row.created_at) }}</span>
        </template>
    </Table>
    <AssignValuesToAttributesModal
        v-if="assignValuesTarget"
        :uuids="assignValuesTarget"
        :integration="integration"
        :category="category"
        :product-filters="productsTable.queryParams"
        :getFilterName="getFilterName"
        :products-list-url="productsListUrl"
        @close="assignValuesTarget = null"
        @updated:filter-items="reloadProductsTable()"
    />
    <AssignValuesBasedOnProductInformation ref="AssignValuesBasedOnProductInformation"
        :integration-category="category"
        @updated:filter-items="reloadProductsTable()"
        :main-integration-language="integration.language_aliases[0]"
        :product-filters="productsTable.queryParams"
        :getFilterName="getFilterName"
        :integration-service="integration.service"
        :products-list-url="productsListUrl"
    />
    <EditProductNameModal ref="EditProductNameModal" :getAvailableTranslations="getAvailableTranslations" :integration="integration" @names:updated="reloadProductsTable()"/>
    <CategoryProductsFiltersModal ref="CategoryProductsFiltersModal" @update:filters="updateFilters" :filters="filters" :integration="integration" :main-integration-language="integration.language_aliases[0]" />
    <GenerateProductKeyWordsModal ref="GenerateProductKeyWordsModal" :integrationLanguages="integration.language_aliases" />
    <AssignAttributesFromIntegrationModal ref="AssignAttributesFromIntegrationModal" @update:table="reloadProductsTable()" :category="category" :integration="integration" />
</template>
<script>
import Button from "../../../../../../components/new/button/AppButton.vue";
import Table from "../../../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../../../components/new/table/tableHandle";
import AppNullableCell from "../../../../../components/tables/AppNullableCell.vue";
import AssignValuesToAttributesModal from "../../../modals/AssignValuesToAttributesModal.vue";
import AssignValuesBasedOnProductInformation from "../../../modals/AssignValuesBasedOnProductInformation.vue";
import CategoryProductsFiltersModal from "./CategoryProductsFiltersModal.vue";
import DateFormatter from "../../../../../../services/DateFormatter";
import DropdownItem from "../../../../../../spa/components/btnDropdown/DropdownItem.vue";
import EditProductNameModal from "../modals/EditProductNameModal.vue";
import GenerateProductKeyWordsModal from "../modals/GenerateProductKeyWordsModal.vue";
import ImageZoomComponent from "../../../../../../components/new/image/ImageZoomComponent.vue";
import AssignAttributesFromIntegrationModal from "../modals/AssignAttributesFromIntegrationModal.vue";

import { PLACEHOLDER_IMAGE_URL } from "../../../../../../constants";
import { IntegrationServiceEnum } from "../../../../../enums/IntegrationServiceEnum";

export default {
    name: "CategoryProductsTable",
    components: {
        Button, Table, AppNullableCell, AssignValuesToAttributesModal, AssignValuesBasedOnProductInformation,
        CategoryProductsFiltersModal, DropdownItem, EditProductNameModal,
        GenerateProductKeyWordsModal, ImageZoomComponent,
        AssignAttributesFromIntegrationModal
    },
    props: {
        integration: {
            type: Object,
            required: true
        },
        category: {
            type: Object,
            required: true
        },
        filters: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            tableHandle: createTableHandle(),
            DateFormatter,
            PLACEHOLDER_IMAGE_URL,
            productsTable: {
                columns: [],
                orders: [{
                    column: 'id',
                    dir: 'desc'
                }],
                queryParams: {
                    filters: []
                }
            },
            selectedProducts: [],
            activeFiltersCount: 0,
            assignValuesTarget: null
        }
    },
    methods: {
        clearFilters() {
            this.$refs.CategoryProductsFiltersModal.clear();
        },
        getAvailableTranslations(objectWithTranslations) {
            return objectWithTranslations[this.mainIntegrationLanguages] ?? Object.values(objectWithTranslations)[0];
        },
        findFilterItemNameInProduct(filter, product) {
            return product.item_names_by_attribute_id?.[filter.id] ?? null;
        },
        reloadProductsTable() {
            this.tableHandle.fetchRows({
                category_uuids: [this.$route.params.categoryUuid],
                extra_fields: ['integrationFilterItems'],
                ...this.productsTable.queryParams
            });
        },
        updateFilters(filters, count = 0) {
            this.activeFiltersCount = count;
            this.productsTable.queryParams = filters;
            this.reloadProductsTable();
        },
        setProductsTableColumns() {
            let productsTableColumns = [
                {key: "created_at", name: this.$t('table.titles.created_at'), width: "150px", type: "string", order: true},
                {key: "id", name: this.$t('table.titles.id'), width: "150px", type: "string", order: true},
                {key: "sku", name: this.$t('table.titles.sku'), width: "150px", type: "string", order: true},
                {key: "image", name: this.$t('table.titles.image'), width: "100px", type: "string", order: false},
                {key: "name", name: this.$t('table.titles.name'), width: "300px", type: "string", order: false},
            ];

            this.filters.forEach((filter) => {
                productsTableColumns.push({
                    key: filter.uuid,
                    name: this.getFilterName(filter),
                    width: "200px",
                    type: "string",
                    order: false
                });
            });

            this.productsTable.columns = productsTableColumns;
        },
        getFilterName(filter) {
            if (!filter) {
                return '';
            }
            
            let name = filter.name;

            if (filter.measurement_unit) {
                name += ` (${filter.measurement_unit})`;
            }

            return name;
        },
        fetchRows() {
            this.reloadProductsTable();
        }
    },
    computed: {
        productsListUrl() {
            const { projectUuid, integrationUuid, categoryUuid } = this.$route.params;
            const url = `/projects/${projectUuid}/pim/channels/${integrationUuid}/categories/${categoryUuid}/publications/list`;

            return url;
        },
        isSupplier() {
            return this.integration.service === IntegrationServiceEnum.SUPPLIER
        },
        mainIntegrationLanguages() {
            return this.integration.language_aliases[0];
        }
    },
    watch: {
        filters: {
            handler() {
                this.setProductsTableColumns();
            },
            deep: true
        }
    },
    mounted() {
        this.setProductsTableColumns();
        this.reloadProductsTable();
    }
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
}
.filter-item-string {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
}
.table-image {
    width: 38px;
    height: 38px;
}
</style>
